import { drawStroke, type BrushStroke } from './brush'

/** A placed sticker, in the percentage units every preview and the bake share. */
export interface StickerPlacement {
  /** Unique per placement, not per sticker — the same art can be placed twice. */
  id: string
  stickerId: string
  url: string
  /** Centre, as a percentage of strip width / height. */
  xPct: number
  yPct: number
  /** Width as a percentage of strip width; height follows the art's own ratio. */
  widthPct: number
  rotation: number
}

/**
 * Draw the guest's stickers onto an exported strip, at print resolution.
 *
 * `PrintPreview` owns the strip itself — photographs, frame art, QR, asset
 * verification and the degraded-render fallback — but it has no notion of
 * stickers. Rather than fork it, the stickers go on afterwards, over its
 * output, which keeps every one of those guarantees intact.
 *
 * Placements are percentages of the strip (see `stickers.ts`), so this works at
 * whatever resolution the export came back at: the same numbers that positioned
 * a 330px-tall preview position a 2400px print.
 *
 * Brush strokes, when the guest drew any, go down first, under the stickers,
 * as the classic editor printed them.
 *
 * Returns the original blob untouched when there is nothing to draw, so the
 * no-sticker session never pays for a re-encode.
 */
export async function bakeStickers(
  stripBlob: Blob,
  placements: StickerPlacement[],
  strokes: BrushStroke[] = []
): Promise<Blob> {
  if (placements.length === 0 && strokes.length === 0) return stripBlob

  const strip = await loadImage(URL.createObjectURL(stripBlob), true)
  const canvas = document.createElement('canvas')
  canvas.width = strip.naturalWidth
  canvas.height = strip.naturalHeight

  const ctx = canvas.getContext('2d')
  if (!ctx) return stripBlob

  ctx.drawImage(strip, 0, 0)
  for (const stroke of strokes) drawStroke(ctx, stroke, canvas.width, canvas.height)

  for (const placement of placements) {
    try {
      const art = await loadImage(placement.url, false)
      const width = (placement.widthPct / 100) * canvas.width
      // Height follows the art's own ratio; a placement only carries a width so
      // a sticker can never be stretched by being moved.
      const height = art.naturalHeight ? width * (art.naturalHeight / art.naturalWidth) : width
      const cx = (placement.xPct / 100) * canvas.width
      const cy = (placement.yPct / 100) * canvas.height

      ctx.save()
      ctx.translate(cx, cy)
      if (placement.rotation) ctx.rotate((placement.rotation * Math.PI) / 180)
      ctx.drawImage(art, -width / 2, -height / 2, width, height)
      ctx.restore()
    } catch {
      // One unreachable sticker must not cost the guest the whole print.
      console.warn('[stickers] sticker skipped, could not load:', placement.url)
    }
  }

  return new Promise<Blob>((resolve) => {
    canvas.toBlob((baked) => resolve(baked ?? stripBlob), 'image/jpeg', 0.95)
  })
}

function loadImage(src: string, revokeAfter: boolean): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image()
    // The strip is a blob URL, but sticker art is served from the CDN, and a
    // tainted canvas cannot be exported at all.
    img.crossOrigin = 'anonymous'
    img.onload = (): void => {
      if (revokeAfter) URL.revokeObjectURL(src)
      resolve(img)
    }
    img.onerror = (): void => {
      if (revokeAfter) URL.revokeObjectURL(src)
      reject(new Error(`Failed to load ${src}`))
    }
    img.src = src
  })
}
